<template>
  <div class="min-h-screen bg-gray-50" :style="cssVars">
    <!-- Loading (initial auth check or booting) -->
    <kiosk-loading v-if="!initialAuthCheckComplete || booting" :error="bootError" />

    <!-- Auth Required -->
    <device-setup v-else-if="needToAuth" :store="store" @authenticated="bootKiosk" />

    <!-- Main Kiosk -->
    <div v-else class="flex flex-col min-h-screen max-w-7xl mx-auto">
      <kiosk-header
        v-if="store.state.teamInfo"
        :team-info="store.state.teamInfo"
        :current-event="store.state.selectedEvent"
        :kiosk-name="headerKioskName"
        @settings="handleSettingsClick"
        @calendar="handleCalendarClick"
      />

      <main
        class="flex-1 px-4 sm:px-6 lg:px-60 py-4 sm:py-6"
        :class="{ 'camera-tile-hidden': shouldHideCameraTile }"
      >
        <!-- Settings Screen -->
        <kiosk-settings
          v-if="showSettings"
          :store="store"
          @back="showSettings = false"
          @settings-changed="handleSettingsChanged"
        />

        <!-- Classes View -->
        <kiosk-classes-view
          v-else-if="showClasses"
          :store="store"
          @back="showClasses = false"
          @view-attendees="handleViewAttendees"
        />

        <!-- Attendees View -->
        <kiosk-attendees-view
          v-else-if="showAttendees"
          ref="attendeesView"
          :key="attendeesRefreshKey"
          :store="store"
          :event="attendeesEvent"
          :selected-date="attendeesSelectedDate"
          @back="handleAttendeesBack"
          @add-wrestler-checkin="handleAddWrestlerCheckIn"
        />

        <!-- Step 1: Select Wrestler -->
        <kiosk-wrestler-search
          v-else-if="!store.state.selectedWrestler"
          ref="wrestlerSearch"
          :store="store"
          :team-name="store.state.teamInfo && store.state.teamInfo.name ? store.state.teamInfo.name : ''"
          :team-logo-url="store.state.teamInfo && store.state.teamInfo.logo_url ? store.state.teamInfo.logo_url : ''"
          :warp-speed="warpSpeed"
          @select="handleWrestlerSelected"
          @search-active="isSearching = $event"
        />

        <!-- Step 2: Select Event for Wrestler -->
        <kiosk-event-selector
          v-else-if="!showQuestionsForm && !checkInSuccess"
          ref="eventSelector"
          :wrestler="store.state.selectedWrestler"
          :events="store.state.todaysEvents"
          :requires-usaw="requiresUsaw"
          :requires-aau="requiresAau"
          :allow-general-check-in="allowGeneralCheckIn"
          :registration-qr-code-url="registrationQrCodeUrl"
          :team-name="store.state.teamInfo && store.state.teamInfo.name ? store.state.teamInfo.name : ''"
          :check-in-logic="checkInLogic"
          :warp-speed="warpSpeed"
          :weight-tracking-enabled="weightTrackingActiveForWrestler"
          @select="handleEventSelected"
          @back="handleBackToSearch"
          @force-checkin="handleForceCheckIn"
          @log-weight="handleLogWeight"
          @modal-state-changed="handleChildModalStateChanged"
          @error-modal-opened="handleErrorModalOpened"
        />

        <!-- Step 3: Custom Questions (if configured) -->
        <kiosk-questions-form
          v-else-if="showQuestionsForm"
          :questions="store.state.checkInQuestions"
          :check-in="successCheckIn"
          :wrestler="successWrestler"
          :kiosk-id="kioskTokenId"
          :endpoint="s3Endpoint"
          :access-key="s3AccessKey"
          :cloudfront-base-url="cloudfrontBaseUrl"
          @complete="handleQuestionsComplete"
          @skip="handleQuestionsSkipped"
          @back="handleQuestionsBack"
        />

        <!-- Step 4: Success Screen -->
        <kiosk-check-in-success
          v-else
          :wrestler="successWrestler"
          :event="successEvent"
          :check-in="successCheckIn"
          :auto-return-seconds="1"
          @return-home="handleReturnHome"
        />
      </main>

      <kiosk-footer
        :scanning="store.state.lookingUpId"
        @switch-club="handleSwitchClubClick"
        @tutorial="handleShowTutorial"
      />

      <!-- Club Switcher (opened behind the current kiosk's admin PIN) -->
      <kiosk-club-switcher
        v-if="showClubSwitcher"
        :kiosks="savedKiosks"
        :active-kiosk-id="kioskTokenId"
        :limited="clubSwitcherLimited"
        @select="handleClubSelected"
        @add="handleAddClubClick"
        @sign-out="handleSignOutOfActiveClub"
        @cancel="showClubSwitcher = false"
      />

      <!-- Add another club -->
      <device-setup
        v-if="showAddClub"
        :store="store"
        mode="add"
        @cancel="handleAddClubCanceled"
      />

      <!-- Admin PIN Modal -->
      <admin-pin-modal
        v-if="showAdminPinModal"
        ref="adminPinModal"
        @verified="handleAdminPinVerified"
        @cancel="handleAdminPinCanceled"
      />

      <!-- Weight Entry Modal -->
      <kiosk-weight-modal
        v-if="showWeightModal"
        :mode="weightModalMode"
        :wrestler-name="store.state.selectedWrestler ? (store.state.selectedWrestler.full_name || store.state.selectedWrestler.display_name || '') : ''"
        :last-weight="store.state.selectedWrestler ? store.state.selectedWrestler.latest_weight : null"
        :last-weight-units="store.state.selectedWrestler ? store.state.selectedWrestler.latest_weight_units : null"
        @confirm="handleWeightConfirmed"
        @cancel="handleWeightCanceled"
        @focused="nativeBridge && nativeBridge.requestShowKeyboard()"
      />

      <!-- Scan Flash Overlay -->
      <transition name="scan-flash">
        <div
          v-if="showScanFlash"
          class="fixed inset-0 pointer-events-none z-50"
          :class="scanFlashType === 'success' ? 'bg-green-500' : 'bg-red-500'"
          style="opacity: 0.3;"
        />
      </transition>

      <!-- Tutorial -->
      <kiosk-tutorial
        v-if="showTutorial"
        :admin-pin="store.state.kiosk && store.state.kiosk.admin_pin ? store.state.kiosk.admin_pin : null"
        :is-in-native-app="isInNativeApp"
        :is-first-launch="tutorialIsFirstLaunch"
        @complete="handleTutorialComplete"
      />

    </div>
  </div>
</template>

<script>
import { useKioskStore } from './composables/useKioskStore';
import { useCheckInLogic } from './composables/useCheckInLogic';
import { useBarcodeScanner } from './composables/useBarcodeScanner';
import { useNativeBridge } from './composables/useNativeBridge';
import { useScanFeedback } from './composables/useScanFeedback';

import DeviceSetup from './components/DeviceSetup.vue';
import KioskLoading from './components/KioskLoading.vue';
import KioskHeader from './components/KioskHeader.vue';
import KioskFooter from './components/KioskFooter.vue';
import KioskWrestlerSearch from './components/KioskWrestlerSearch.vue';
import KioskEventSelector from './components/KioskEventSelector.vue';
import KioskCheckInSuccess from './components/KioskCheckInSuccess.vue';
import KioskQuestionsForm from './components/KioskQuestionsForm.vue';
import AdminPinModal from './components/AdminPinModal.vue';
import KioskWeightModal from './components/KioskWeightModal.vue';
import KioskSettings from './components/KioskSettings.vue';
import KioskClassesView from './components/KioskClassesView.vue';
import KioskAttendeesView from './components/KioskAttendeesView.vue';
import KioskTutorial from './components/KioskTutorial.vue';
import KioskClubSwitcher from './components/KioskClubSwitcher.vue';

export default {
  name: 'kiosk-app',
  components: {
    DeviceSetup,
    KioskLoading,
    KioskHeader,
    KioskFooter,
    KioskWrestlerSearch,
    KioskEventSelector,
    KioskCheckInSuccess,
    KioskQuestionsForm,
    AdminPinModal,
    KioskWeightModal,
    KioskSettings,
    KioskClassesView,
    KioskAttendeesView,
    KioskTutorial,
    KioskClubSwitcher,
  },
  data() {
    return {
      // Booting screen
      booting: true,
      bootError: false,
      initialAuthCheckComplete: false,

      // Store instance
      store: null,

      // Check-in logic
      checkInLogic: null,

      // Barcode scanner
      barcodeScanner: null,

      // Native bridge (for iOS kiosk app)
      nativeBridge: null,

      // Scan feedback (visual flash + native sound)
      scanFeedback: null,
      showScanFlash: false,
      scanFlashType: 'success', // 'success' or 'error'

      // S3 configuration for file uploads (from meta tags or env)
      s3Endpoint: null,
      s3AccessKey: null,
      cloudfrontBaseUrl: null,

      // Event refresh
      eventRefreshInterval: null,
      refreshingEvents: false,

      // Self-update: the kiosk build this page loaded with (KioskController#build_id)
      // and the pending re-check after the server reported a different one
      loadedBuildId: null,
      buildRecheckTimeout: null,

      // Admin PIN modal
      showAdminPinModal: false,
      pendingForceCheckIn: null,
      adminPinPurpose: null, // 'force-checkin', 'settings', 'calendar', or 'switch-club'

      // Multi-club switching
      showClubSwitcher: false,
      clubSwitcherLimited: false, // true when the kiosk has no admin PIN - sign-out only
      showAddClub: false,
      savedKiosks: [],

      // Weight tracking modal
      showWeightModal: false,
      weightModalMode: 'check-in', // 'check-in' or 'standalone'
      // Check-in waiting on a weight: { event, override: { pin, reason } | null }
      pendingWeightCheckIn: null,
      // Weight already entered for a check-in the server then blocked (paused/
      // overdue/weekly limit), so the admin override doesn't ask for it again.
      blockedCheckInWeight: null, // { eventId, wrestlerId, weightEntry }
      // Today's check-ins for the selected wrestler, used to skip the weight prompt on a
      // repeat scan of the same event (the server re-uses that check-in and keeps its weigh-in)
      selectedWrestlerCheckInsToday: [],

      // Child component modal state (for native bridge)
      childModalOpen: false,

      // Whether the wrestler search has a non-empty query — used to hide the
      // native camera tile while the user is clearly searching by name (not scanning)
      isSearching: false,

      // Settings
      showSettings: false,
      showClasses: false,
      showAttendees: false,
      attendeesEvent: null,
      attendeesSelectedDate: null,
      attendeesRefreshKey: 0, // Increment to force attendees view to refresh
      kioskSettings: {
        requireUsaw: true,
        requireAau: true,
        allowGeneralCheckIn: false,
        warpSpeed: false,
      },

      // Check-in success state
      checkInSuccess: false,
      successWrestler: null,
      successEvent: null,
      successCheckIn: null,

      // Custom questions state
      showQuestionsForm: false,

      // Tutorial
      showTutorial: false,
      tutorialIsFirstLaunch: false,
    };
  },
  mounted() {
    // Initialize store
    this.store = useKioskStore();

    // See if we are authed - refresh from server to get latest data
    try {
      // Picks the active club from the saved kiosks (migrating legacy single-kiosk storage)
      const kiosk = this.store.loadActiveKiosk();
      if (kiosk) {
        // Set auth header from cached data first
        axios.defaults.headers.common.Authorization = `Bearer ${kiosk.team.api_key}`;
        // Mark auth check complete (will show loading while booting)
        this.initialAuthCheckComplete = true;
        // Refresh kiosk data from server to get latest fields
        this.refreshKioskData(kiosk.id);
      } else {
        // No cached kiosk - show auth screen
        this.initialAuthCheckComplete = true;
        this.booting = false;
      }
    } catch (e) {
      // loadActiveKiosk already recovers from corrupt JSON, so this is a storage write
      // failure - only drop the legacy mirror and keep every saved club
      localStorage.removeItem('wiq_kiosk');
      axios.defaults.headers.common.Authorization = 'Bearer';
      this.initialAuthCheckComplete = true;
      this.booting = false;
    }

    // Load S3 config from meta tags if available
    this.loadS3Config();
    this.loadedBuildId = document.querySelector('meta[name="kiosk-build"]')?.getAttribute('content') || null;
  },
  created() {
    // Will be initialized once store is ready
  },
  computed: {
    // Sitting on the wrestler search screen with nothing open or typed, so a
    // reload can't interrupt anyone
    isIdle() {
      return !this.store?.state.selectedWrestler
        && !this.isSearching
        && !this.childModalOpen
        && !this.showSettings
        && !this.showClasses
        && !this.showAttendees
        && !this.showClubSwitcher
        && !this.showAddClub
        && !this.showAdminPinModal
        && !this.showWeightModal
        && !this.showTutorial
        && !this.showQuestionsForm
        && !this.checkInSuccess;
    },
    cssVars() {
      return {
        '--team-primary': this.store?.state.teamInfo?.brand_color || '#374151',
        '--team-text': this.store?.state.teamInfo?.brand_text_color || '#ffffff',
      };
    },
    needToAuth() {
      return this.store?.state.kiosk === null;
    },
    teamInfoHasUsawRequired() {
      return this.store?.state.teamInfo && this.store.state.teamInfo.usaw_memberships !== 'usaw_disabled';
    },
    teamInfoHasAauRequired() {
      return this.store?.state.teamInfo && this.store.state.teamInfo.aau_memberships && this.store.state.teamInfo.aau_memberships !== 'aau_disabled';
    },
    requiresUsaw() {
      // Team must have USAW enabled AND kiosk setting must require it
      return this.teamInfoHasUsawRequired && this.kioskSettings.requireUsaw;
    },
    requiresAau() {
      // Team must have AAU enabled AND kiosk setting must require it
      return this.teamInfoHasAauRequired && this.kioskSettings.requireAau;
    },
    allowGeneralCheckIn() {
      return this.kioskSettings.allowGeneralCheckIn;
    },
    // Non-null while a scan must be ignored: a flow is in progress for the selected wrestler
    scanBlockedMessage() {
      if (this.showWeightModal) return 'Finish logging this weight before scanning another card';
      if (this.showAdminPinModal) return 'Finish entering the admin PIN before scanning another card';
      if (this.showQuestionsForm) return 'Finish the check-in questions before scanning another card';
      return null;
    },
    weightTrackingEnabled() {
      return !!this.store?.state.teamInfo?.weight_tracking_enabled;
    },
    // Team flag AND the selected wrestler is not opted out through their rosters
    // (weight_tracking_active is computed server side on the kiosk wrestler payload)
    weightTrackingActiveForWrestler() {
      if (!this.weightTrackingEnabled) return false;
      const wrestler = this.store?.state.selectedWrestler;
      if (!wrestler) return false;
      return wrestler.weight_tracking_active !== false;
    },
    warpSpeed() {
      return this.kioskSettings.warpSpeed;
    },
    kioskTokenId() {
      return this.store?.state.kiosk?.id || null;
    },
    // Only worth showing which kiosk this is once the device holds several clubs
    headerKioskName() {
      return this.savedKiosks.length > 1 ? this.store?.state.kiosk?.name || null : null;
    },
    registrationQrCodeUrl() {
      return this.store?.state.kiosk?.team?.registration_qr_code_url || null;
    },
    // Track whether the native camera tile should be hidden — triggered by any
    // modal/overlay being open, or by the wrestler search having an active query
    // (they're clearly searching by name rather than scanning).
    shouldHideCameraTile() {
      return this.showAdminPinModal
        || this.showWeightModal
        || this.showClubSwitcher
        || this.showAddClub
        || this.childModalOpen
        || this.showTutorial
        || this.isSearching
        || this.showAttendees
        || this.showSettings
        || this.showClasses
        || this.showQuestionsForm
        || this.checkInSuccess;
    },
    // Check if running in native app
    isInNativeApp() {
      return this.nativeBridge && this.nativeBridge.isInNativeApp && this.nativeBridge.isInNativeApp();
    },
  },
  watch: {
    needToAuth() {
      if (!this.needToAuth && this.booting) {
        this.bootKiosk();
      }
    },
    // Notify native app when the camera tile should be hidden (modals open or user searching).
    // Reuses setModalOpen on the bridge — the native app hides the tile whenever this is true.
    shouldHideCameraTile(isHidden) {
      if (this.nativeBridge) {
        this.nativeBridge.setModalOpen(isHidden);
      }
    },
  },
  methods: {
    loadS3Config() {
      // Try to load S3 config from meta tags (similar to how webview embeds work)
      const endpointMeta = document.querySelector('meta[name="s3-endpoint"]');
      const accessKeyMeta = document.querySelector('meta[name="s3-access-key"]');
      const cloudfrontMeta = document.querySelector('meta[name="cloudfront-base-url"]');

      if (endpointMeta) this.s3Endpoint = endpointMeta.getAttribute('content');
      if (accessKeyMeta) this.s3AccessKey = accessKeyMeta.getAttribute('content');
      if (cloudfrontMeta) this.cloudfrontBaseUrl = cloudfrontMeta.getAttribute('content');
    },
    loadKioskSettings() {
      try {
        const savedSettings = JSON.parse(localStorage.getItem(this.store.settingsKey()));
        if (savedSettings) {
          this.kioskSettings = {
            ...this.kioskSettings,
            ...savedSettings,
          };
        }

        // One-time migration from legacy kiosk settings
        this.migrateLegacySettings();
      } catch (e) {
        console.error('Failed to load kiosk settings:', e);
      }
    },
    migrateLegacySettings() {
      try {
        // Migrate force_usaw_off from legacy kiosk to new settings
        const legacyForceUsawOff = JSON.parse(localStorage.getItem('force_usaw_off'));
        if (legacyForceUsawOff === true) {
          // force_usaw_off: true means USAW was NOT required, so requireUsaw should be false
          this.kioskSettings.requireUsaw = false;
          // Save the migrated setting
          localStorage.setItem(this.store.settingsKey(), JSON.stringify(this.kioskSettings));
        }
        // Remove legacy key regardless of value (one-time migration)
        localStorage.removeItem('force_usaw_off');
      } catch (e) {
        // Ignore migration errors - not critical
        console.error('Failed to migrate legacy kiosk settings:', e);
      }
    },
    refreshKioskData(kioskId) {
      const vm = this;
      axios.get(this.$apiService.kioskTokenUrl(kioskId))
        .then((response) => {
          // Update localStorage with fresh data (merged so admin_pin from the session survives)
          vm.store.setActiveKiosk(response.data.kiosk);
          vm.bootKiosk();
        })
        .catch((error) => {
          console.error('Failed to refresh kiosk data:', error);
          const status = error.response?.status;
          if (![401, 403, 404].includes(status)) {
            // Network blip or server error - keep the saved clubs and let staff reload
            vm.bootError = true;
            return;
          }

          // Kiosk token was deleted or invalid - forget just this club
          const remaining = vm.store.removeSavedKiosk(kioskId);
          if (remaining.length > 0) {
            vm.store.setActiveKiosk(remaining[0]);
            window.location.reload();
            return;
          }

          // Nothing left - force re-auth
          vm.store.clearActiveKiosk();
          vm.booting = false;
        });
    },
    bootKiosk() {
      const vm = this;
      console.log('booting kiosk called');

      this.savedKiosks = this.store.getSavedKiosks();

      // Initialize check-in logic once we have store
      this.checkInLogic = useCheckInLogic(this.store, this.$apiService, this.$notificationManager);

      // Initialize barcode scanner
      this.barcodeScanner = useBarcodeScanner(
        this.store,
        this.$apiService,
        this.$notificationManager,
        this.handleWrestlerScanned,
        {
          // A scan mid-flow would swap the wrestler under it (the weigh-in or PIN override would
          // then save for the scanned wrestler), and its keystrokes would land in the focused input
          isScanBlocked: () => !!this.scanBlockedMessage,
          onBlockedScan: this.notifyScanBlocked,
        },
      );
      this.barcodeScanner.setupScanner();

      // Initialize native bridge (for iOS kiosk app QR scanning)
      this.nativeBridge = useNativeBridge(
        this.store,
        this.$apiService,
        this.$notificationManager,
        this.handleWrestlerScanned,
      );
      this.nativeBridge.setupBridge();

      // Initialize scan feedback (visual flash + native sound)
      this.scanFeedback = useScanFeedback(this.store);
      this.scanFeedback.onFlash(this.handleScanFlash);

      Promise.all([this.loadEvents(), this.loadAllRegistrationQuestions(), this.loadTeam()])
        .then((results) => {
          const events = results[0];
          vm.store.setTodaysEvents(events);

          const questions = results[1];
          vm.store.setCheckInQuestions(questions);

          const team = results[2];
          vm.store.setTeamInfo(team);

          // Load kiosk settings from localStorage
          vm.loadKioskSettings();

          // Start background event refresh
          vm.startEventRefreshInterval();

          vm.booting = false;

          // Show tutorial if first time after authentication
          const tutorialSeen = localStorage.getItem('wiq_kiosk_tutorial_seen');
          if (!tutorialSeen) {
            vm.tutorialIsFirstLaunch = true;
            vm.showTutorial = true;
          }
        })
        .catch((error) => {
          console.log(error);
          vm.bootError = true;
        });
    },
    loadTeam() {
      return axios.get(this.$apiService.teamUrl(this.store.state.kiosk.team.id))
        .then((response) => Promise.resolve(response.data))
        .catch((error) => Promise.reject(error));
    },
    loadAllRegistrationQuestions() {
      const vm = this;
      const url = vm.$apiService.registrationQuestions('CheckIn');

      return vm.$apiService.loadAllPages(url, 'registration_questions')
        .then((objects) => Promise.resolve(objects))
        .catch((error) => Promise.reject(error));
    },
    loadEvents() {
      const vm = this;
      const start = moment().startOf('day').toISOString();
      const end = moment().endOf('day').toISOString();
      const params = `?&start=${start}&end=${end}&preventCache=${new Date()}`;
      const url = this.$apiService.eventsUrl(params);
      return this.$apiService.loadAllPages(url, 'events')
        .then((events) => {
          // Track which date these events are for
          vm.store.state.eventsLoadedDate = moment().format('YYYY-MM-DD');
          return Promise.resolve(events);
        })
        .catch((error) => Promise.reject(error));
    },
    startEventRefreshInterval() {
      // Refresh events every 45 minutes in the background
      const FORTY_FIVE_MINUTES = 45 * 60 * 1000;
      this.eventRefreshInterval = setInterval(() => {
        this.refreshEventsInBackground();
      }, FORTY_FIVE_MINUTES);
    },
    stopEventRefreshInterval() {
      if (this.eventRefreshInterval) {
        clearInterval(this.eventRefreshInterval);
        this.eventRefreshInterval = null;
      }
      if (this.buildRecheckTimeout) {
        clearTimeout(this.buildRecheckTimeout);
        this.buildRecheckTimeout = null;
      }
    },
    refreshEventsInBackground() {
      // Silently refresh events without showing loading state
      // Only refresh if we're on the wrestler search screen (not mid-flow)
      if (this.store.state.selectedWrestler) {
        return; // Don't refresh while user is mid-flow
      }

      this.checkForNewBuild();

      this.loadEvents()
        .then((events) => {
          this.store.setTodaysEvents(events);
          console.log('Events refreshed in background');
        })
        .catch((error) => {
          console.error('Background event refresh failed:', error);
        });
    },
    async fetchServerBuildId() {
      // Same origin as the kiosk page, not the API base URL
      const response = await fetch('/build', { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Build check failed with status ${response.status}`);
      }
      const data = await response.json();
      return data.build_id;
    },
    // Reloads onto new kiosk code once it ships, so tablets left open all day
    // don't keep running an old bundle. A mismatch is confirmed after the
    // rolling deploy window, since mid-deploy an old dyno can still answer.
    async checkForNewBuild() {
      if (!this.loadedBuildId || this.buildRecheckTimeout) {
        return;
      }

      try {
        const buildId = await this.fetchServerBuildId();
        if (buildId === this.loadedBuildId) {
          return;
        }
      } catch (error) {
        console.error('Kiosk build check failed:', error);
        return;
      }

      const FIVE_MINUTES = 5 * 60 * 1000;
      this.buildRecheckTimeout = setTimeout(async () => {
        this.buildRecheckTimeout = null;
        try {
          const buildId = await this.fetchServerBuildId();
          // Not idle yet: the next background refresh tries again
          if (buildId !== this.loadedBuildId && this.isIdle) {
            window.location.reload();
          }
        } catch (error) {
          console.error('Kiosk build re-check failed:', error);
        }
      }, FIVE_MINUTES);
    },
    isEventsStale() {
      // Check if loaded events are from a different day
      const loadedDate = this.store.state.eventsLoadedDate;
      const today = moment().format('YYYY-MM-DD');
      return loadedDate !== today;
    },
    async ensureFreshEvents() {
      // If events are stale (from a different day), refresh them
      if (!this.isEventsStale()) {
        return; // Events are fresh, nothing to do
      }

      console.log('Events are stale, refreshing...');
      this.refreshingEvents = true;

      try {
        const events = await this.loadEvents();
        this.store.setTodaysEvents(events);
      } catch (error) {
        console.error('Failed to refresh events:', error);
      } finally {
        this.refreshingEvents = false;
      }
    },
    async handleWrestlerSelected(wrestler) {
      // Ensure events are fresh before proceeding
      await this.ensureFreshEvents();

      this.store.selectWrestler(wrestler);
      this.loadSelectedWrestlerCheckIns(wrestler);
    },
    notifyScanBlocked() {
      this.$notificationManager.emit('show-toast', this.scanBlockedMessage, false);
    },
    async handleWrestlerScanned(wrestler) {
      // The camera tile is hidden on these screens, but guard anyway: a scan must not change the
      // wrestler a pending weigh-in or PIN override will be saved for
      if (this.scanBlockedMessage) {
        this.notifyScanBlocked();
        return;
      }

      // Trigger scan feedback (visual flash + native sound)
      if (this.scanFeedback) {
        this.scanFeedback.triggerScanFeedback('success');
      }

      // Called from barcode scanner - also ensure fresh events
      await this.ensureFreshEvents();
      this.store.selectWrestler(wrestler);
      this.loadSelectedWrestlerCheckIns(wrestler);
    },
    // Non-blocking: if this fails we fall back to prompting, and the server ignores a
    // nested weight on a check-in that already has one.
    loadSelectedWrestlerCheckIns(wrestler) {
      this.selectedWrestlerCheckInsToday = [];
      if (!this.weightTrackingEnabled || !wrestler) return;

      const url = `${this.$apiService.checkInUrl()}?for_day=${new Date().toISOString()}&wrestler_profile_id=${wrestler.id}&per_page=50`;
      axios.get(url)
        .then((response) => {
          // Only keep them if the same wrestler is still selected
          if (this.store.state.selectedWrestler?.id === wrestler.id) {
            this.selectedWrestlerCheckInsToday = response.data.check_ins || [];
          }
        })
        .catch((error) => {
          console.error('Failed to load today\'s check-ins for weight prompt:', error);
        });
    },
    handleScanFlash(type) {
      // Handle visual flash from scan feedback
      this.scanFlashType = type;
      this.showScanFlash = true;

      // Auto-hide after brief flash
      setTimeout(() => {
        this.showScanFlash = false;
      }, 200);
    },
    // Whether a weight entry is required before this check-in can post.
    // Roster opt-out is wrestler based (see WrestlerProfile#weight_tracking_opted_out?),
    // and a repeat scan of an event the wrestler already weighed in for skips the prompt.
    weightPromptNeeded(event) {
      if (!this.weightTrackingActiveForWrestler) return false;

      return !this.alreadyWeighedInFor(event);
    },
    alreadyWeighedInFor(event) {
      const isGeneric = !event || event.id === -1;
      return this.selectedWrestlerCheckInsToday.some((checkIn) => {
        if (!checkIn.weight_entry) return false;
        if (!isGeneric) return checkIn.event_id === event.id;

        // Generic check-ins are debounced server side for 5 minutes; inside that window the
        // same check-in is re-used, so only skip when the weigh-in is that recent
        return checkIn.event_id === null
          && moment().diff(moment(checkIn.created_at), 'minutes') < 5;
      });
    },
    async handleEventSelected(event) {
      // Collect a weight first when the team requires it for this event
      if (this.weightPromptNeeded(event)) {
        this.pendingWeightCheckIn = { event, override: null };
        this.weightModalMode = 'check-in';
        this.showWeightModal = true;
        return;
      }

      // Post check-in directly when event is selected
      await this.performCheckIn(this.store.state.selectedWrestler, event);
    },
    async performCheckIn(wrestler, event, weightEntry = null) {
      try {
        const checkIn = await this.checkInLogic.postCheckIn(wrestler, event, 'kiosk_self', weightEntry);

        // Store success info for the success screen
        this.successWrestler = wrestler;
        this.successEvent = event;
        this.successCheckIn = checkIn;

        // Check if we have custom questions to ask
        if (this.store.state.checkInQuestions && this.store.state.checkInQuestions.length > 0) {
          // Show questions step before success
          this.showQuestionsForm = true;
        } else if (this.warpSpeed) {
          // Warp speed: skip success screen, return to search immediately
          this.handleReturnHome();
        } else {
          // No questions, go directly to success
          this.checkInSuccess = true;
        }
      } catch (error) {
        console.error('Failed to complete check-in:', error);
        // Any error other than a paused/overdue/weekly-limit block already got
        // a toast from postCheckIn.
        this.showCheckInBlocked(error, wrestler, event, weightEntry);
      }
    },
    // Opens the matching override modal when the server blocked a check-in
    // (paused/overdue/weekly limit). Returns false for any other error.
    showCheckInBlocked(error, wrestler, event, weightEntry) {
      const code = this.checkInLogic.checkInBlockerCode(error);
      if (!code) return false;

      if (!this.$refs.eventSelector) {
        this.$notificationManager.emit('show-toast', 'Failed to check in. Please try again.', false);
        return true;
      }

      // Keep the weight so the admin override doesn't ask for it again
      this.blockedCheckInWeight = weightEntry
        ? { eventId: event?.id, wrestlerId: wrestler.id, weightEntry }
        : null;
      this.$refs.eventSelector.showCheckInBlockedModal(event, code);
      return true;
    },
    handleReturnHome() {
      // Reset all state and return to wrestler search
      this.checkInSuccess = false;
      this.showQuestionsForm = false;
      this.successWrestler = null;
      this.successEvent = null;
      this.successCheckIn = null;
      this.showWeightModal = false;
      this.pendingWeightCheckIn = null;
      this.blockedCheckInWeight = null;
      this.selectedWrestlerCheckInsToday = [];
      this.store.resetFlow();

      // Focus the search input after Vue re-renders the wrestler search component
      this.$nextTick(() => {
        if (this.$refs.wrestlerSearch) {
          this.$refs.wrestlerSearch.resetAndFocus();
        }
        // Request native keyboard for auto-return scenario (timer-based returns lack user gesture context)
        this.nativeBridge.requestShowKeyboard();
      });
    },
    handleQuestionsComplete() {
      // Questions answered
      this.showQuestionsForm = false;
      if (this.warpSpeed) {
        this.handleReturnHome();
      } else {
        this.checkInSuccess = true;
      }
    },
    handleQuestionsSkipped() {
      // Questions skipped (all were optional)
      this.showQuestionsForm = false;
      if (this.warpSpeed) {
        this.handleReturnHome();
      } else {
        this.checkInSuccess = true;
      }
    },
    handleQuestionsBack() {
      // Go back from questions to event selection
      // Note: This discards the check-in that was just created
      this.showQuestionsForm = false;
      this.successWrestler = null;
      this.successEvent = null;
      this.successCheckIn = null;
      // Don't reset the full flow - keep the wrestler selected so they can choose another event
    },
    handleBackToSearch() {
      // Go back to wrestler search and focus the input
      this.blockedCheckInWeight = null;
      this.store.resetFlow();

      this.$nextTick(() => {
        if (this.$refs.wrestlerSearch) {
          this.$refs.wrestlerSearch.resetAndFocus();
        }
      });
    },
    handleForceCheckIn(payload) {
      // This will be called when user clicks "Check in anyway" for USAW or roster issues
      console.log('Force check-in requested:', payload);

      // Store the payload and show PIN modal (server will validate if PIN is configured)
      this.pendingForceCheckIn = payload;
      this.adminPinPurpose = 'force-checkin';
      this.showAdminPinModal = true;
    },
    handleChildModalStateChanged(isOpen) {
      // Track modal state from child components (e.g., KioskEventSelector eligibility modals)
      this.childModalOpen = isOpen;
    },
    handleErrorModalOpened(errorType) {
      // Play error sound when an error modal is shown (subscription paused/overdue, USAW, AAU, roster)
      console.log('Error modal opened:', errorType);
      if (this.scanFeedback) {
        this.scanFeedback.triggerScanFeedback('error');
      }
    },
    handleSettingsClick() {
      console.log('Settings clicked');

      // Show PIN modal for settings access (server will validate if PIN is configured)
      this.adminPinPurpose = 'settings';
      this.showAdminPinModal = true;
    },
    handleSwitchClubClick() {
      // Show PIN modal for the club menu (server will validate the current kiosk's PIN)
      this.adminPinPurpose = 'switch-club';
      this.showAdminPinModal = true;
    },
    openClubSwitcher(limited) {
      this.savedKiosks = this.store.getSavedKiosks();
      this.clubSwitcherLimited = limited;
      this.showClubSwitcher = true;
    },
    handleClubSelected(kiosk) {
      if (kiosk.id === this.kioskTokenId) {
        this.showClubSwitcher = false;
        return;
      }
      // Full reload so nothing from the previous club (scanner, bridge, caches) carries over
      this.store.setActiveKiosk(kiosk);
      window.location.reload();
    },
    handleAddClubClick() {
      this.showClubSwitcher = false;
      this.showAddClub = true;
      // Typed kiosk codes shouldn't be read as badge scans; a successful add reloads anyway
      if (this.barcodeScanner) {
        this.barcodeScanner.teardownScanner();
      }
    },
    handleAddClubCanceled() {
      this.showAddClub = false;
      if (this.barcodeScanner) {
        this.barcodeScanner.setupScanner();
      }
    },
    handleSignOutOfActiveClub() {
      const remaining = this.store.removeSavedKiosk(this.kioskTokenId);
      if (remaining.length > 0) {
        this.store.setActiveKiosk(remaining[0]);
        window.location.reload();
        return;
      }
      this.logOut();
    },
    handleCalendarClick() {
      console.log('Calendar clicked');

      // Show PIN modal for calendar/attendees access (server will validate if PIN is configured)
      this.adminPinPurpose = 'calendar';
      this.showAdminPinModal = true;
    },
    handleViewAttendees({ event, selectedDate }) {
      // Navigate to the attendees view
      this.attendeesEvent = event;
      this.attendeesSelectedDate = selectedDate;
      this.showClasses = false;
      this.showAttendees = true;
    },
    handleAttendeesBack() {
      // Go back to the classes view
      this.showAttendees = false;
      this.attendeesEvent = null;
      this.attendeesSelectedDate = null;
      this.showClasses = true;
    },
    async handleAddWrestlerCheckIn(wrestler) {
      // Admin is adding a wrestler directly from the attendees view - skip all eligibility checks
      try {
        await this.checkInLogic.postCheckIn(wrestler, this.attendeesEvent, 'kiosk_admin_calendar');

        // Refresh the attendees list and reset the inline search.
        // The re-rendered list with the new wrestler is its own success feedback.
        this.attendeesRefreshKey++;
      } catch (error) {
        console.error('Failed to add wrestler:', error);
        this.$notificationManager.emit(
          'show-toast',
          'Failed to add wrestler. Please try again.',
          false,
        );
        // Reset inline search state so they can try again
        this.$nextTick(() => {
          if (this.$refs.attendeesView) {
            this.$refs.attendeesView.checkingInWrestler = false;
          }
        });
      }
    },
    async handleAdminPinVerified(enteredPin) {
      if (this.adminPinPurpose === 'settings') {
        // Verify PIN server-side for settings access
        try {
          await this.verifyPinServerSide(enteredPin, 'settings_access');
          this.showAdminPinModal = false;
          this.showClasses = false;
          this.showAttendees = false;
          this.showSettings = true;
        } catch (error) {
          // Show error in PIN modal
          if (this.$refs.adminPinModal) {
            this.$refs.adminPinModal.showError();
          }
          return;
        }
        this.adminPinPurpose = null;
        return;
      }

      if (this.adminPinPurpose === 'switch-club') {
        try {
          await this.verifyPinServerSide(enteredPin, 'switch_club');
          this.showAdminPinModal = false;
          this.openClubSwitcher(false);
        } catch (error) {
          if (error.response?.status === 422) {
            // No admin PIN configured - allow sign-out only, never an unprotected switch
            this.showAdminPinModal = false;
            this.openClubSwitcher(true);
          } else {
            // Show error in PIN modal
            if (this.$refs.adminPinModal) {
              this.$refs.adminPinModal.showError();
            }
            return;
          }
        }
        this.adminPinPurpose = null;
        return;
      }

      if (this.adminPinPurpose === 'calendar') {
        // Verify PIN server-side for calendar/attendees access
        try {
          await this.verifyPinServerSide(enteredPin, 'calendar_access');
          this.showAdminPinModal = false;
          this.showSettings = false;
          this.showClasses = true;
        } catch (error) {
          // Show error in PIN modal
          if (this.$refs.adminPinModal) {
            this.$refs.adminPinModal.showError();
          }
          return;
        }
        this.adminPinPurpose = null;
        return;
      }

      // Force check-in with admin override
      if (!this.pendingForceCheckIn) {
        this.showAdminPinModal = false;
        this.adminPinPurpose = null;
        return;
      }

      const { event, type } = this.pendingForceCheckIn;
      // Only reuse a weight entered by this same wrestler for this same event
      const blocked = this.blockedCheckInWeight;
      const blockedWeight = blocked
        && blocked.eventId === event?.id
        && blocked.wrestlerId === this.store.state.selectedWrestler?.id
        ? blocked.weightEntry
        : null;

      // When a weight is required, the check-in POST (which normally validates
      // the PIN) is deferred until after the weight modal. Verify the PIN
      // server-side now so a bad PIN still surfaces in the PIN modal.
      if (!blockedWeight && this.weightPromptNeeded(event)) {
        try {
          await this.verifyPinServerSide(enteredPin, 'force_checkin');
        } catch (error) {
          if (this.$refs.adminPinModal) {
            this.$refs.adminPinModal.showError();
          }
          return;
        }
        this.showAdminPinModal = false;
        this.pendingForceCheckIn = null;
        this.adminPinPurpose = null;
        this.pendingWeightCheckIn = { event, override: { pin: enteredPin, reason: type } };
        this.weightModalMode = 'check-in';
        this.showWeightModal = true;
        return;
      }

      // Perform check-in with admin override (server validates PIN)
      try {
        await this.performCheckInWithOverride(
          this.store.state.selectedWrestler,
          event,
          enteredPin,
          type,
          blockedWeight,
        );
        this.showAdminPinModal = false;
        this.pendingForceCheckIn = null;
        this.adminPinPurpose = null;
      } catch (error) {
        console.error('Failed to complete check-in with override:', error);
        if (error.response?.status === 422) {
          // Invalid PIN - show error in modal
          if (this.$refs.adminPinModal) {
            this.$refs.adminPinModal.showError();
          }
        } else {
          // Other error - close modal and show toast
          this.showAdminPinModal = false;
          this.pendingForceCheckIn = null;
          this.adminPinPurpose = null;
          this.$notificationManager.emit('show-toast', 'Failed to check in. Please try again.', false);
        }
      }
    },
    handleAdminPinCanceled() {
      this.showAdminPinModal = false;
      this.pendingForceCheckIn = null;
      this.adminPinPurpose = null;
      // Reset modal state
      if (this.$refs.adminPinModal) {
        this.$refs.adminPinModal.resetState();
      }
    },
    handleLogWeight() {
      // Standalone weigh-in from the Log weight button (no check-in)
      this.weightModalMode = 'standalone';
      this.pendingWeightCheckIn = null;
      this.showWeightModal = true;
    },
    async handleWeightConfirmed(weightEntry) {
      this.showWeightModal = false;

      if (this.weightModalMode === 'standalone') {
        await this.postStandaloneWeight(weightEntry);
        return;
      }

      const pending = this.pendingWeightCheckIn;
      this.pendingWeightCheckIn = null;
      if (!pending) return;

      if (pending.override) {
        try {
          await this.performCheckInWithOverride(
            this.store.state.selectedWrestler,
            pending.event,
            pending.override.pin,
            pending.override.reason,
            weightEntry,
          );
        } catch (error) {
          console.error('Failed to complete check-in with override:', error);
          this.$notificationManager.emit('show-toast', 'Failed to check in. Please try again.', false);
        }
      } else {
        await this.performCheckIn(this.store.state.selectedWrestler, pending.event, weightEntry);
      }
    },
    handleWeightCanceled() {
      // Cancel aborts the pending check-in and stays on the event selector
      this.showWeightModal = false;
      this.pendingWeightCheckIn = null;
    },
    async postStandaloneWeight(weightEntry) {
      try {
        await axios.post(this.$apiService.weightEntriesUrl(), {
          weight_entry: {
            wrestler_profile_id: this.store.state.selectedWrestler.id,
            ...weightEntry,
          },
        });
        this.$notificationManager.emit('show-toast', 'Weight logged', true);
        // Back to the search screen, like a successful check-in
        this.handleReturnHome();
      } catch (error) {
        console.error('Failed to log weight:', error);
        this.$notificationManager.emit('show-toast', 'Failed to log weight. Please try again.', false);
      }
    },
    async verifyPinServerSide(pin, purpose) {
      const url = this.$apiService.kioskTokenVerifyPinUrl(this.kioskTokenId);
      return axios.post(url, {
        kiosk_token: {
          pin,
          purpose,
        },
      });
    },
    async performCheckInWithOverride(wrestler, event, adminPin, overrideReason, weightEntry = null) {
      let checkIn;
      try {
        checkIn = await this.checkInLogic.postCheckInWithOverride(
          wrestler,
          event,
          adminPin,
          this.kioskTokenId,
          overrideReason,
          weightEntry,
        );
      } catch (error) {
        // An override for a roster/USAW/AAU issue doesn't waive a billing
        // block, so the server can still refuse; ask for that override next.
        if (this.showCheckInBlocked(error, wrestler, event, weightEntry)) return;
        throw error;
      }

      // Store success info for the success screen
      this.successWrestler = wrestler;
      this.successEvent = event;
      this.successCheckIn = checkIn;

      // Check if we have custom questions to ask
      if (this.store.state.checkInQuestions && this.store.state.checkInQuestions.length > 0) {
        // Show questions step before success
        this.showQuestionsForm = true;
      } else if (this.warpSpeed) {
        // Warp speed: skip success screen, return to search immediately
        this.handleReturnHome();
      } else {
        // No questions, go directly to success
        this.checkInSuccess = true;
      }
    },
    handleSettingsChanged(settings) {
      // Settings have been changed, update local state
      console.log('Settings changed:', settings);
      this.kioskSettings = {
        ...this.kioskSettings,
        ...settings,
      };
    },
    handleTutorialComplete() {
      localStorage.setItem('wiq_kiosk_tutorial_seen', 'true');
      this.showTutorial = false;
    },
    handleShowTutorial() {
      this.tutorialIsFirstLaunch = false;
      this.showTutorial = true;
    },
    logOut() {
      if (this.barcodeScanner) {
        this.barcodeScanner.teardownScanner();
      }
      if (this.nativeBridge) {
        this.nativeBridge.teardownBridge();
      }
      this.stopEventRefreshInterval();
      this.showClubSwitcher = false;
      this.showAddClub = false;
      // Clear tutorial seen flag so it shows again on next sign-in
      localStorage.removeItem('wiq_kiosk_tutorial_seen');
      this.store.clearActiveKiosk();
    },
  },
  beforeDestroy() {
    if (this.barcodeScanner) {
      this.barcodeScanner.teardownScanner();
    }
    if (this.nativeBridge) {
      this.nativeBridge.teardownBridge();
    }
    this.stopEventRefreshInterval();
  },
};
</script>
